import React, { Component } from "react";
import { withModulesManager, withHistory, formatMessage } from "@openimis/fe-core";
import { injectIntl } from "react-intl";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import {
  Typography,
  Paper,
  Box,
  Grid,
  IconButton,
  Card,
  CardContent,
  CircularProgress,
} from "@material-ui/core";
import { ArrowBack as BackIcon, Edit as EditIcon } from "@material-ui/icons";
import { withStyles } from "@material-ui/core/styles";
import {
  fetchHealthFacilityEnrollment,
  submitHealthFacilityEnrollment,
  reviewHealthFacilityEnrollment,
  approveHealthFacilityEnrollment,
  rejectHealthFacilityEnrollment,
  turnToDraftHealthFacilityEnrollment,
  deleteHealthFacilityEnrollment,
} from "../../actions";
import EnrollmentStatusBadge from "../../components/common/EnrollmentStatusBadge";
import EnrollmentActionButtons from "../../components/common/EnrollmentActionButtons";
import KeyValueSection from "../../components/common/KeyValueSection";
import {
  facilityFieldRows,
  enrollmentMetaRows,
  openCreatedEnrollmentEntity,
} from "../../utils/enrollmentData";

const styles = (theme) => ({
  page: { margin: theme.spacing(2) },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(2),
    gap: theme.spacing(2),
  },
  titleWrap: { display: "flex", alignItems: "center", gap: theme.spacing(2), flex: 1 },
  title: { fontWeight: 700, color: theme.palette.primary.main },
  card: { marginBottom: theme.spacing(2) },
});

class HealthFacilityEnrollmentDetail extends Component {
  componentDidMount() {
    this.load();
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.match?.params?.enrollmentId !==
      this.props.match?.params?.enrollmentId
    ) {
      this.load();
    }
  }

  load = () => {
    const uuid = this.props.match?.params?.enrollmentId;
    if (uuid) {
      this.props.fetchHealthFacilityEnrollment(this.props.modulesManager, uuid);
    }
  };

  handleBack = () => {
    const { history } = this.props;
    if (history) history.push("/enrollment/healthfacility");
    else window.location.href = "/enrollment/healthfacility";
  };

  handleEdit = () => {
    const uuid = this.props.enrollment?.uuid;
    if (!uuid) return;
    const { history } = this.props;
    if (history) history.push(`/enrollment/healthfacility/${uuid}/edit`);
    else window.location.href = `/enrollment/healthfacility/${uuid}/edit`;
  };

  handleOpenCreated = () => {
    const { modulesManager, history, enrollment } = this.props;
    openCreatedEnrollmentEntity(
      modulesManager,
      history,
      "healthFacility",
      enrollment
    );
  };

  run = (action, payload) =>
    Promise.resolve(action(this.props.modulesManager, payload)).then(() =>
      this.load()
    );

  render() {
    const { classes, intl, enrollment } = this.props;
    if (!enrollment) {
      return (
        <Box className={classes.page} display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      );
    }

    const facilityRows = facilityFieldRows(enrollment.facilityData);
    const metaRows = enrollmentMetaRows(enrollment);
    const created = enrollment.createdHealthFacility;

    return (
      <Box className={classes.page}>
        <Box className={classes.header}>
          <Box className={classes.titleWrap}>
            <IconButton onClick={this.handleBack}>
              <BackIcon />
            </IconButton>
            <Typography variant="h5" className={classes.title}>
              {formatMessage(intl, "enrollment", "healthFacilityEnrollment.detail") ||
                "Health Facility Enrollment"}
            </Typography>
            <EnrollmentStatusBadge status={enrollment.status} />
          </Box>
          {enrollment.status === "DRAFT" && (
            <IconButton color="primary" onClick={this.handleEdit}>
              <EditIcon />
            </IconButton>
          )}
        </Box>

        <Card className={classes.card}>
          <CardContent>
            <EnrollmentActionButtons
              intl={intl}
              enrollment={enrollment}
              onEdit={this.handleEdit}
              onOpenCreated={
                enrollment.createdHealthFacility?.uuid
                  ? this.handleOpenCreated
                  : undefined
              }
              openCreatedLabel="Open created health facility"
              onSubmit={(p) =>
                this.run(this.props.submitHealthFacilityEnrollment, p)
              }
              onReview={(p) =>
                this.run(this.props.reviewHealthFacilityEnrollment, p)
              }
              onApprove={(p) =>
                this.run(this.props.approveHealthFacilityEnrollment, p)
              }
              onReject={(p) =>
                this.run(this.props.rejectHealthFacilityEnrollment, p)
              }
              onTurnToDraft={(p) =>
                this.run(this.props.turnToDraftHealthFacilityEnrollment, p)
              }
              onDelete={(uuid) =>
                this.run(this.props.deleteHealthFacilityEnrollment, uuid).then(
                  this.handleBack
                )
              }
            />
          </CardContent>
        </Card>

        <Grid container spacing={2}>
          <Grid item xs={12} md={7}>
            <Paper className={classes.card}>
              <Box p={2}>
                <KeyValueSection title="Facility details" rows={facilityRows} />
                {created && (
                  <KeyValueSection
                    title="Created facility"
                    rows={[
                      { label: "Code", value: created.code || "—" },
                      { label: "Name", value: created.name || "—" },
                      { label: "UUID", value: created.uuid || "—" },
                    ]}
                  />
                )}
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} md={5}>
            <Paper className={classes.card}>
              <Box p={2}>
                <KeyValueSection title="Enrollment info" rows={metaRows} />
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    );
  }
}

const mapStateToProps = (state) => ({
  enrollment: state.enrollment?.healthFacilityEnrollmentDetail,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      fetchHealthFacilityEnrollment,
      submitHealthFacilityEnrollment,
      reviewHealthFacilityEnrollment,
      approveHealthFacilityEnrollment,
      rejectHealthFacilityEnrollment,
      turnToDraftHealthFacilityEnrollment,
      deleteHealthFacilityEnrollment,
    },
    dispatch
  );

export default withModulesManager(
  withHistory(
    injectIntl(
      withStyles(styles)(
        connect(
          mapStateToProps,
          mapDispatchToProps
        )(HealthFacilityEnrollmentDetail)
      )
    )
  )
);
